import React, { useState, useEffect, useCallback, useRef } from 'react';
// import CompanySearch from '../components/CompanySearch';
import '../App.css';
import { apiClient, API_CONFIG } from '../api/config';
import Typography from 'antd/es/typography';
import { logger } from '../utils/logger';
import { Card, Alert } from 'antd';
import { SearchOutlined, RobotOutlined, UpOutlined, DownOutlined, GlobalOutlined } from '@ant-design/icons';
import activityTracker from '../user/ActivityTracker';
import { useAILimit } from '../user/AIButtonLimitChecker';
import AIUpgradePrompt from '../components/AIUpgradePrompt';
import { useCompany } from '../context/CompanyContext';
import { getFromCacheE, saveExpiryToCache } from '../utils/cacheUtils';
import { useLocation } from 'react-router-dom';
import { getModuleAPIConfig } from '../api/gpt-config';


// 处理英文段落
const processEnglishParagraph = (paragraph) => {
  // 将文本按句点分割，但保留句点
  const sentences = paragraph.split(/(?<=[.])/).filter(s => s.trim());
  let currentLine = '';
  const lines = [];
  const maxLineLength = 100;

  sentences.forEach(sentence => {
    // 检查是否以句点结尾
    if (sentence.endsWith('.')) {
      // 检查是否是小数点或缩写
      const isDecimalPoint = /\d\.\d/.test(sentence);
      const isAbbreviation = /[A-Z]\.[A-Z]/.test(sentence) || /[A-Z]\.$/.test(sentence);
      
      if (!isDecimalPoint && !isAbbreviation) {
        // 处理当前句子，确保不超过最大长度
        const words = sentence.split(' ');
        words.forEach(word => {
          if ((currentLine + ' ' + word).length <= maxLineLength) {
            currentLine = currentLine ? currentLine + ' ' + word : word;
          } else {
            lines.push(currentLine);
            currentLine = word;
          }
        });
        
        // 添加当前行并换行
        if (currentLine) {
          lines.push(currentLine);
          currentLine = '';
        }
      } else {
        // 如果是小数点或缩写，直接添加到当前行
        if ((currentLine + ' ' + sentence).length <= maxLineLength) {
          currentLine = currentLine ? currentLine + ' ' + sentence : sentence;
        } else {
          lines.push(currentLine);
          currentLine = sentence;
        }
      }
    } else {
      // 非句点结尾的文本，按长度处理
      if ((currentLine + ' ' + sentence).length <= maxLineLength) {
        currentLine = currentLine ? currentLine + ' ' + sentence : sentence;
      } else {
        lines.push(currentLine);
        currentLine = sentence;
      }
    }
  });

  // 添加最后一行
  if (currentLine) {
    lines.push(currentLine);
  }

  return (
    <React.Fragment>
      {lines.map((line, index) => (
        <React.Fragment key={index}>
          {line}
          <br />
        </React.Fragment>
      ))}
    </React.Fragment>
  );
};

// 处理中文段落
const processChineseParagraph = (paragraph) => {
  // 按中文句号分割并保留句号
  const sentences = paragraph.split(/(。)/).filter(s => s);
  
  return (
    <React.Fragment>
      {sentences.map((sentence, index) => (
        <React.Fragment key={index}>
          {sentence}
          {sentence === '。' && <br />}
        </React.Fragment>
      ))}
    </React.Fragment>
  );
};

function App_transcript({ initialSymbol, transcriptData: parentTranscriptData, setTranscriptData: setParentTranscriptData }) {
  const { companyInfo, updateCompanyInfo } = useCompany();
  const location = useLocation();
  const [symbol, setSymbol] = useState(companyInfo?.symbol || '');
  const [formData, setFormData] = useState({
    symbol: companyInfo?.symbol || '',
    year: '',
    quarter: ''
  });
  const [transcriptData, setTranscriptData] = useState({
    transcripts: [],
    isLoading: false,
    error: null,
    AI_transcript: {
      executive: '',
      qa: ''
    }
  });
  const [isTranslated, setIsTranslated] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);
  const [displayMode, setDisplayMode] = useState('both'); // 'both', 'cn', 'en'

  // 添加 GPT 相關的狀態
  const [gptAnalysis, setGptAnalysis] = useState({
    executiveAnalysis: '',
    qaAnalysis: '',
    isLoading: false,
    error: null
  });

  // 添加緩衝區狀態，用於存儲待顯示的文本
  const [textBuffer, setTextBuffer] = useState({
    executive: '',
    qa: ''
  });

  // 添加折疊/展開狀態
  const [isExecutiveExpanded, setIsExecutiveExpanded] = useState(true);
  const [isQAExpanded, setIsQAExpanded] = useState(true);
  
  // 添加兩個 refs 用於跟踪打字效果的定時器
  const typingTimerExec = useRef(null);
  const typingTimerQA = useRef(null);
  
  // 添加一個控制打字速度的變量（每個字符的毫秒數）
  const typingSpeed = 10; // 可以調整這個值來改變速度

  // 網路獲取逐字稿狀態
  const [webTranscript, setWebTranscript] = useState({
    isLoading: false,
    error: null,
    result: '',
    isVisible: false
  });
  
  // 獲取 API 配置（使用 news 模組的配置，預設為 Perplexity）
  const { API_KEY, API_HOST, API_MODEL, API_NAME, API_MAX_TOKENS } = getModuleAPIConfig('news');

  // 季度選項 - 反向順序排列（Q4, Q3, Q2, Q1）
  const quarters = [
    { value: '', label: '請選擇季度' },
    { value: 'Q4', label: 'Q4' },
    { value: 'Q3', label: 'Q3' },
    { value: 'Q2', label: 'Q2' },
    { value: 'Q1', label: 'Q1' }
  ];

  // 如果需要同時支持舊格式（1、2、3、4）和新格式（Q1、Q2、Q3、Q4），可以添加以下轉換函數
  const formatQuarter = (quarter) => {
    if (!quarter) return '';
    
    // 如果已經是 Q 開頭的格式，直接返回
    if (quarter.toString().startsWith('Q')) {
      return quarter;
    }
    
    // 將數字轉換為 Q 格式
    return `Q${quarter}`;
  };

  // 在 API 請求中使用的季度值可能需要轉換
  // 例如，如果後端 API 仍然需要數字格式（1、2、3、4），則需要添加反向轉換
  const getNumericQuarter = (quarter) => {
    if (!quarter) return '';
    
    // 從 Q 格式轉換回數字
    if (quarter.toString().startsWith('Q')) {
      return quarter.substring(1);
    }
    
    return quarter;
  };

  // 在组件顶部添加 QA 计数器
  const qaCounter = useRef(0);


  // 使用 useCallback 包装 fetchTranscript 函数
  const fetchTranscript = useCallback(async (e, symbolParam, yearParam, quarterParam, forceRefresh = false) => {
    if (e) e.preventDefault();
    
    const symbolToUse = symbolParam || formData.symbol;
    const yearToUse = yearParam || formData.year;
    const quarterToUse = quarterParam || formData.quarter;

    const numericQuarter = getNumericQuarter(quarterToUse);
    
    logger.log(`使用的参数: symbolToUse=${symbolToUse}, yearToUse=${yearToUse}, quarterToUse=${quarterToUse}`);
    
    if (!yearToUse || !quarterToUse) {
      setTranscriptData(prev => ({
        ...prev,
        error: '請選擇財務年度和季度',
        transcripts: [] // 清空舊的查詢結果
      }));
      return;
    }
    
    try {
      // 设置加载状态
      setTranscriptData(prev => ({
        ...prev,
        isLoading: true,
        error: null
      }));
      
      // 添加活动追踪
      activityTracker.trackBtnExecution(
        `fetch-transcript_${formData.year}_${formData.quarter}`,
        formData.symbol || companyInfo?.symbol,
        window.location.pathname
      );
      // 从API获取数据
      logger.log(`從API獲取逐字稿數據: ${symbolToUse}, ${yearToUse}, ${quarterToUse}`);
      
      // 获取英文内容
      const enResponse = await apiClient.get(
        `${API_CONFIG.endpoints.transcript}/${symbolToUse}`,
        {
          params: {
            year: yearToUse,
            quarter: numericQuarter,
            lang: 'en'
          }
        }
      );
      const enData = enResponse.data;

      // 获取中文内容
      const twResponse = await apiClient.get(
        `${API_CONFIG.endpoints.transcript}/${symbolToUse}`,
        {
          params: {
            year: yearToUse,
            quarter: numericQuarter,
            lang: 'tw'
          }
        }
      );
      let twData = twResponse.data;

      // 如果没有中文资料，进行翻译
      if (!Array.isArray(twData) || twData.length === 0) {
        logger.log('未找到中文內容，開始翻譯...');
        twData = await translateContent(enData);
        setIsTranslated(true);
      } else {
        setIsTranslated(false);
      }

      // 解析并组织数据
      const processedData = processTranscripts(enData, twData);

      // 更新状态
      const newTranscriptData = {
        transcripts: processedData,
        isLoading: false,
        error: null,
        symbol: symbolToUse,
        year: yearToUse,
        quarter: quarterToUse,
        AI_transcript: null // 添加新字段用于存储 AI 分析内容
      };
      
      // 保存到缓存
      const cacheKey = `transcript_${symbolToUse}`;
      saveExpiryToCache(cacheKey, newTranscriptData, 90);
      logger.log('保存数据到缓存:', cacheKey);
      
      setTranscriptData(newTranscriptData);
      
      // 如果有父组件传递的setTranscriptData函数，也更新父组件状态
      if (setParentTranscriptData) {
        setParentTranscriptData(newTranscriptData);
      }
      
      // 成功获取数据后重置 QA 计数器
      qaCounter.current = 0;
      
    } catch (error) {
      let errorMessage = '獲取數據時發生錯誤';
      // 如果是 404 错误，显示友好提示
      if (error.response && error.response.status === 404) {
        errorMessage = '資料庫無此資料,請確認後重新查詢';
      } else if (error.message) {
        errorMessage = `獲取數據失敗：${error.message}`;
        logger.error('獲取逐字稿失敗:', error);
      }
      
      // 更新错误状态
      setTranscriptData(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
        transcripts: [] // 清空旧的查询结果
      }));
    }
  }, [formData.symbol, formData.year, formData.quarter, companyInfo?.symbol]);

  // 使用 useCallback 包装 fetchAvailableYears 函数
  const fetchAvailableYears = useCallback(async () => {
    try {
      // 确保使用正确的 symbol
      const symbolToUse = companyInfo?.symbol;
      if (!symbolToUse) {
        logger.warn('无法获取年度数据：未提供公司代码');
        return;
      }

      logger.log(`正在獲取財務年度範圍，公司代碼: ${symbolToUse}`);
      const apiUrl = `${API_CONFIG.endpoints.companyYears.replace(':symbol', symbolToUse)}`;
      logger.log(`API URL: ${apiUrl}`);

      const response = await apiClient.get(apiUrl);
      logger.log(`獲取到的財務年度數據: ${response.data}`);

      if (response.data && Array.isArray(response.data)) {
        // 检查数据结构
        logger.log(`數據項示例: ${response.data[0]}`);

        // 将获取到的年度数据转换为下拉选单需要的格式
        const years = [
          { value: '', label: '請選擇' }, // 添加空选项
          ...response.data.map(item => {
            const yearValue = item.fiscalyear || item.FiscalYear || item.year || item;
            return {
              value: yearValue.toString(),
              label: yearValue.toString()
            };
          })
        ];

        setAvailableYears(years);
      } else {
        logger.warn(`API返回的數據不是數組或為空: ${response.data}`);
        // 使用默认年度范围
        setAvailableYears([
          { value: '', label: '請選擇' }, // 添加空选项
          ...Array.from({ length: 8 }, (_, i) => ({ value: (2026 - i).toString(), label: (2026 - i).toString() }))
        ]);
      }
    } catch (error) {
      logger.error(`獲取財務年度範圍失敗: ${error}`);
      // 使用默认年度范围
      setAvailableYears([
        { value: '', label: '請選擇' }, // 添加空选项
        ...Array.from({ length: 8 }, (_, i) => ({ value: (2026 - i).toString(), label: (2026 - i).toString() }))
      ]);
    }
  }, [companyInfo?.symbol]);

  // 使用 useCallback 包装 fetchLatestTranscript 函数
  const fetchLatestTranscript = useCallback(async () => {
    const symbolToUse = companyInfo?.symbol;
    
    setTranscriptData(prev => ({
      ...prev,
      isLoading: true,
      error: null
    }));
    
    try {
      // 使用正确的 API 路径
      const response = await apiClient.get(`${API_CONFIG.endpoints.latestTranscript}/${symbolToUse}`);

      if (response.data.error) {
        throw new Error(response.data.error);
      }

      const { fiscalYear, quarter, eventDate } = response.data;
      console.log('解構後的數據:', { fiscalYear, quarter, eventDate });

      // 更新表单数据
      setFormData(prev => ({
        ...prev,
        symbol: symbolToUse,
        year: fiscalYear,
        quarter: formatQuarter(quarter)
      }));

      // 格式化日期
      const formatDate = (dateString) => {
        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) {
            return '日期格式錯誤1';
          }
          return date.toLocaleDateString('zh-TW', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour12: false
          });
        } catch (error) {
          logger.error('日期格式化錯誤:', error);
          return '日期格式錯誤2';
        }
      };

      // 更新最新财报信息
      setTranscriptData(prev => ({
        ...prev,
        latestInfo: {
          fiscalYear,
          quarter: formatQuarter(quarter),
          eventDate: formatDate(eventDate)
        },
        isLoading: false
      }));

    } catch (error) {
      logger.error('获取最新财报会议记录失败:', error);
      setTranscriptData(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || '获取最新财报会议记录失败'
      }));
    }
  }, [companyInfo?.symbol]);

  // 回存緩存資料 
  useEffect(() => {
    if (companyInfo?.symbol) {
      // 记录页面访问
      activityTracker.trackPageView(companyInfo.symbol);
      
      // 检查缓存
      const transcriptCacheKey = `transcript_${companyInfo.symbol}`;
      const aiTranscriptCacheKey = `AI-transcript_${companyInfo.symbol}`;
      const formCacheKey = `form_${companyInfo.symbol}`;
      
      const cachedTranscript = getFromCacheE(transcriptCacheKey);
      const cachedAITranscript = getFromCacheE(aiTranscriptCacheKey);
      const cachedForm = getFromCacheE(formCacheKey);

      fetchAvailableYears();
      // 恢复表单数据
      if (cachedForm) {
        logger.log('从缓存恢复表单数据:', formCacheKey);
        setFormData(prev => ({
          ...prev,
          symbol: companyInfo.symbol,
          year: cachedForm.year,
          quarter: cachedForm.quarter
        }));
      } 

      if (cachedAITranscript) {
        logger.log('从缓存加载AI分析数据:', aiTranscriptCacheKey);
        setGptAnalysis({
          executiveAnalysis: cachedAITranscript.executive,
          qaAnalysis: cachedAITranscript.qa,
          isLoading: false,
          error: null
        });
        setTextBuffer({
          executive: cachedAITranscript.executive,
          qa: cachedAITranscript.qa
        });
      }

      if (cachedTranscript) {
        logger.log('从缓存加载财报数据:', transcriptCacheKey);
        setTranscriptData({
          ...cachedTranscript,
          isLoading: false,
          error: null
        });

      } else {
        // 清空之前的状态
        setTranscriptData({
          transcripts: [],
          isLoading: false,
          error: null,
          AI_transcript: {
            executive: '',
            qa: ''
          }
        });
      }

      // 获取最新数据
      fetchLatestTranscript();
    }
  }, [companyInfo?.symbol]);

  // 组件卸载时重置状态
  useEffect(() => {
    return () => {
      setTranscriptData({
        transcripts: [],
        isLoading: false,
        error: null,
        AI_transcript: {
          executive: '',
          qa: ''
        }
      });
    };
  }, []);

  // 当 symbol 变化时，检查是否为空并清除数据
  useEffect(() => {
    // 如果 symbol 为空，清空数据
    if (!symbol) {
      setTranscriptData({
        transcripts: [],
        isLoading: false,
        error: null,
        AI_transcript: {
          executive: '',
          qa: ''
        }
      });
      setIsTranslated(false);
    }
    // 如果有 symbol，这里不需要主动获取数据，因为:
    // 1. 组件中其他 useEffect 已经处理了数据加载
    // 2. 不存在 fetchTranscriptData 函数，应使用 fetchLatestTranscript
  }, [symbol]);

  // 處理 URL 參數中的 symbol
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlSymbol = searchParams.get('symbol');
    
    // 優先使用 URL 參數，其次是 initialSymbol，最後是 companyInfo
    const symbolToUse = urlSymbol || initialSymbol || companyInfo?.symbol;
    
    if (symbolToUse && symbolToUse !== companyInfo?.symbol) {
      logger.log('從 URL 參數或 initialSymbol 獲取到新的 symbol:', symbolToUse);
      
      // 更新 CompanyContext
      updateCompanyInfo({
        symbol: symbolToUse,
        company_name: '',
        sector: '',
        industry: '',
        image: ''
      });
      
      // 更新本地狀態
      setSymbol(symbolToUse);
      setFormData(prev => ({
        ...prev,
        symbol: symbolToUse
      }));
      
      logger.log('已更新 symbol 到 CompanyContext 和本地狀態');
    }
  }, [location.search, initialSymbol, companyInfo?.symbol, updateCompanyInfo]);

  // 使用获取到的可用年度，如果没有则使用默认值
  const years = availableYears.length > 0 
    ? availableYears 
    : [
        { value: '', label: '請選擇' }, // 添加空选项
        ...Array.from({ length: 8 }, (_, i) => ({ value: (2026 - i).toString(), label: (2026 - i).toString() }))
      ];
  

  // 修改 handleInputChange 函数，在表单数据变化时保存到缓存
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // 特殊處理季度輸入，確保使用 Q 格式
    if (name === 'quarter' && value && !value.startsWith('Q') && !isNaN(value)) {
      setFormData(prev => {
        const newFormData = {
          ...prev,
          [name]: `Q${value}`
        };
        // 保存到缓存
        if (companyInfo?.symbol) {
          const formCacheKey = `form_${companyInfo.symbol}`;
          saveExpiryToCache(formCacheKey, {
            year: newFormData.year,
            quarter: newFormData.quarter
          }, 90);
          logger.log('保存表单数据到缓存:', formCacheKey);
        }
        return newFormData;
      });
    } else {
      setFormData(prev => {
        const newFormData = {
          ...prev,
          [name]: value
        };
        // 保存到缓存
        if (companyInfo?.symbol) {
          const formCacheKey = `form_${companyInfo.symbol}`;
          saveExpiryToCache(formCacheKey, {
            year: newFormData.year,
            quarter: newFormData.quarter
          }, 90);
          logger.log('保存表单数据到缓存:', formCacheKey);
        }
        return newFormData;
      });
    }
  };

  // 初始化時轉換季度值為 Q 格式
  useEffect(() => {
    // 如果表單中的季度值是數字格式，則轉換為 Q 格式
    if (formData.quarter && !formData.quarter.startsWith('Q') && !isNaN(formData.quarter)) {
      setFormData(prev => ({
        ...prev,
        quarter: `Q${prev.quarter}`
      }));
    }
  }, []);

  // 修改翻譯函數
  const translateContent = async (enData) => {
    try {
      logger.log('\n開始翻譯處理');
      logger.debug('------------------------');
      logger.debug('原始 enData:', enData);

      // 替換所有 enData elements 中的換行符為特殊標記
      const processedEnData = enData.map(transcript => ({
        ...transcript,
        content: transcript.content.replace(/\n/g, ' {{NL}} ')
      }));

      logger.debug('\n替換換行符後的 enData:');
      logger.debug('------------------------');
      logger.debug(processedEnData);

      // 提取所有要翻譯的文本
      const textsToTranslate = processedEnData.map(transcript => transcript.content);

      const response = await apiClient.post(API_CONFIG.endpoints.translate, {
        texts: textsToTranslate
      });

      const { translations } = response.data;

      logger.debug('\n翻譯結果：');
      logger.debug('------------------------');
      logger.debug(translations);

      // 將翻譯結果轉換為 twData 格式，並將特殊標記恢復為換行符
      const twData = processedEnData.map((transcript, index) => ({
        ...transcript,
        lang: 'tw',
        content: translations[index].replace(/{{NL}}/g, '\n')
      }));

      // 恢復 enData 中的換行符
      enData.forEach(transcript => {
        transcript.content = transcript.content.replace(/{{NL}}/g, '\n');
      });

      logger.log('\n最終處理完畢');
      logger.debug('------------------------');
      logger.debug('處理後的 enData:', enData);
      logger.debug('生成的 twData:', twData);
      logger.debug('------------------------\n');

      return twData;
    } catch (error) {
      logger.error('翻譯錯誤:', error);
      return null;
    }
  };

  // 處理和組織逐字稿數據
  const processTranscripts = (enData, twData) => {
    logger.log('\n開始處理逐字稿數據');
    logger.debug('------------------------');
    logger.debug('輸入的 enData:', enData);
    logger.debug('輸入的 twData:', twData);

    const processedGroups = [];

    // 按照索引順序處理數據
    enData.forEach((enItem, index) => {
      const twItem = twData[index]; // 獲取對應索引的中文數據

      // 分割並過濾英文和中文段落
      const enParagraphs = enItem.content.split('\n').filter(p => p.trim());
      const twParagraphs = twItem ?
        twItem.content.split('\n').filter(p => p.trim()) :
        Array(enParagraphs.length).fill('');

      // 將配對的數據存入 processedGroups
      processedGroups.push({
        contentType: enItem.contenttype,
        enParagraphs,
        twParagraphs,
        eventdate: enItem.eventdate, // 保存會議問答
        index
      });
    });

    logger.log('\n處理完畢');
    logger.debug('------------------------');
    logger.debug('最終分組結果:', processedGroups);
    logger.debug('------------------------\n');

    return processedGroups;
  };

  // 添加 WebSocket 相關狀態和連接管理
  const wsRef = useRef(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [wsError, setWsError] = useState(null);
  const { checkAILimit, showUpgradePrompt, setShowUpgradePrompt, limitInfo } = useAILimit();

  // 移除預先建立的 WebSocket 連接，改為在需要時動態創建
  // 這樣可以避免部署環境中的連接問題

  // 修改 fetchGptAnalysis 函數，參考 Income.js 的實現方式
  const fetchGptAnalysis = async () => {
    const symbolToUse = formData.symbol || companyInfo?.symbol;
    const yearToUse = formData.year;
    const quarterToUse = formData.quarter;
    
    // AI 使用次數檢查（使用 hook）
    const limitCheck = await checkAILimit('AI-transcript');
    if (!limitCheck.canProceed) {
      setGptAnalysis(prev => ({
        ...prev,
        error: limitCheck.reason || 'AI 使用次數已達上限'
      }));
      return;
    }

    // 檢查是否有必要的數據
    if (transcriptData.transcripts.length === 0) {
      setGptAnalysis(prev => ({
        ...prev,
        error: '沒有財報數據，請先查詢財報內容'
      }));
      return;
    }

    // 其它檢查保持不變
    if (!symbolToUse || !yearToUse || !quarterToUse) {
      logger.error('缺少必要的查詢參數', { symbolToUse, yearToUse, quarterToUse });
      setGptAnalysis(prev => ({
        ...prev,
        error: '請先查詢財報內容'
      }));
      return;
    }

    // 追蹤按鈕點擊（通過檢查後再記錄）
    activityTracker.trackBtnExecution(
      `AI-transcript_${formData.year}_${formData.quarter}`,
      formData.symbol || companyInfo?.symbol,
      window.location.pathname
    );

    // 清空緩衝區和分析結果
    setTextBuffer({
      executive: '',
      qa: ''
    });
    
    setGptAnalysis(prev => ({
      ...prev,
      executiveAnalysis: '',
      qaAnalysis: '',
      isLoading: true,
      error: null
    }));

    // 確保計時器引用被重置為 null
    if (typingTimerExec.current) {
      clearTimeout(typingTimerExec.current);
      typingTimerExec.current = null;
    }
    
    if (typingTimerQA.current) {
      clearTimeout(typingTimerQA.current);
      typingTimerQA.current = null;
    }

    // 重置累積內容
    accumulatedContent.current = {
      executive: '',
      qa: ''
    };

    // 參考 Income.js 的 WebSocket 連接方式
    const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const wsHost = window.location.hostname === 'localhost'
      ? 'localhost:5080'
      : window.location.host;
    const wsUrl = `${wsProtocol}://${wsHost}/ws-app`;

    logger.log('開始 GPT 分析，WebSocket URL:', wsUrl);
    logger.log('當前數據：', {
      symbol: symbolToUse,
      year: yearToUse,
      quarter: quarterToUse
    });

    // 如果沒有現有連接或連接已關閉，創建新連接
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      logger.log('創建新的 WebSocket 連接');
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        logger.log('WebSocket 連接已建立');
        setWsConnected(true);
        setWsError(null);
        
        // 連接建立後立即發送請求
        const message = JSON.stringify({
          type: 'transcript-analysis',
          symbol: symbolToUse,
          fiscalYear: yearToUse,
          quarter: quarterToUse
        });
        
        logger.log('發送 WebSocket 請求:', message);
        wsRef.current.send(message);
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          logger.debug('收到 WebSocket 消息:', data);

          if (data.error) {
            logger.error('WebSocket 錯誤:', data.error);
            setGptAnalysis(prev => ({
              ...prev,
              isLoading: false,
              error: data.error
            }));
            return;
          }

          if (data.type === 'complete') {
            const aiTranscriptData = {
              executive: accumulatedContent.current.executive,
              qa: accumulatedContent.current.qa
            };

            logger.log('AI分析完成，保存內容:', {
              executive_length: accumulatedContent.current.executive?.length,
              qa_length: accumulatedContent.current.qa?.length,
            });

            setTranscriptData(prev => ({
              ...prev,
              AI_transcript: aiTranscriptData
            }));

            setGptAnalysis(prev => ({
              ...prev,
              isLoading: false
            }));

            const cacheKey = `AI-transcript_${symbolToUse}`;
            saveExpiryToCache(cacheKey, aiTranscriptData, 90);
            logger.log('保存AI分析数据到缓存:', cacheKey);
            return;
          }

          if (data.type && data.content) {
            if (data.type === 'executive') {
              accumulatedContent.current.executive += data.content;
            } else if (data.type === 'qa') {
              accumulatedContent.current.qa += data.content;
            }

            setTextBuffer(prev => ({
              ...prev,
              executive: data.type === 'executive' 
                ? prev.executive + data.content 
                : prev.executive,
              qa: data.type === 'qa' 
                ? prev.qa + data.content 
                : prev.qa
            }));
          }
        } catch (error) {
          logger.error('處理 WebSocket 消息時出錯:', error);
        }
      };
      
      wsRef.current.onclose = (event) => {
        logger.warn(`WebSocket 連接已關閉: ${event.code} - ${event.reason}`);
        setWsConnected(false);
      };

      wsRef.current.onerror = (error) => {
        logger.error('WebSocket 錯誤!', error);
        setWsError('WebSocket 連接錯誤，請刷新頁面重試');
        setGptAnalysis(prev => ({
          ...prev,
          isLoading: false,
          error: 'WebSocket 連接錯誤，請刷新頁面重試'
        }));
      };
    } else {
      // 如果連接已存在且開啟，直接發送請求
      logger.log('使用現有 WebSocket 連接');
      const message = JSON.stringify({
        type: 'transcript-analysis',
        symbol: symbolToUse,
        fiscalYear: yearToUse,
        quarter: quarterToUse
      });
      
      logger.log('發送 WebSocket 請求:', message);
      wsRef.current.send(message);
    }
  };

  // 修改 useEffect 添加問答分析的打字機效果
  useEffect(() => {
    logger.debug('緩衝區更新觸發打字效果檢查:', {
      execBuffer: textBuffer.executive?.length,
      execAnalysis: gptAnalysis.executiveAnalysis?.length,
      qaBuffer: textBuffer.qa?.length,
      qaAnalysis: gptAnalysis.qaAnalysis?.length
    });
    
    // 當緩衝區有內容且與當前顯示的內容不同時，立即開始/重新開始打字機效果
    if (textBuffer.executive && textBuffer.executive !== gptAnalysis.executiveAnalysis) {
      if (typingTimerExec.current) clearTimeout(typingTimerExec.current);
      
      // 打字机效果函数
      const typeText = function recursiveType(sourceText, displayedText, setter, timerRef) {
        // 檢查是否有新內容添加到源文本
        if (sourceText !== textBuffer.executive) {
          // 如果源文本已更新，使用最新的源文本重新啟動打字
          recursiveType(textBuffer.executive, displayedText, setter, timerRef);
          return;
        }
        
        // 不是全部文本已显示，继续输出
        if (displayedText.length < sourceText.length) {
          const charsToAdd = Math.min(5, sourceText.length - displayedText.length);
          const nextText = sourceText.substring(0, displayedText.length + charsToAdd);
          
          setter(nextText);
          
          timerRef.current = setTimeout(() => {
            recursiveType(sourceText, nextText, setter, timerRef);
          }, typingSpeed);
        } else {
          timerRef.current = null;
        }
      };
      
      // 开始执行打字机效果
      typeText(
        textBuffer.executive, 
        gptAnalysis.executiveAnalysis || '', 
        (text) => setGptAnalysis(prev => ({ ...prev, executiveAnalysis: text })),
        typingTimerExec
      );
    }
    
    // 这里应该添加依赖数组，避免无限循环
  }, [textBuffer.executive, gptAnalysis.executiveAnalysis, typingSpeed]);

  // 为问答部分添加完整的 useEffect 实现
  useEffect(() => {
    logger.debug('QA 緩衝區更新觸發打字效果檢查:', {
      qaBuffer: textBuffer.qa?.length,
      qaAnalysis: gptAnalysis.qaAnalysis?.length
    });
    
    // 當緩衝區有內容且與當前顯示的內容不同時，開始/重新開始打字機效果
    if (textBuffer.qa && textBuffer.qa !== gptAnalysis.qaAnalysis) {
      if (typingTimerQA.current) clearTimeout(typingTimerQA.current);
      
      // 打字机效果函数
      const typeText = function recursiveType(sourceText, displayedText, setter, timerRef) {
        // 檢查是否有新內容添加到源文本
        if (sourceText !== textBuffer.qa) {
          // 如果源文本已更新，使用最新的源文本重新啟動打字
          recursiveType(textBuffer.qa, displayedText, setter, timerRef);
          return;
        }
        
        // 不是全部文本已显示，继续输出
        if (displayedText.length < sourceText.length) {
          const charsToAdd = Math.min(5, sourceText.length - displayedText.length);
          const nextText = sourceText.substring(0, displayedText.length + charsToAdd);
          
          setter(nextText);
          
          timerRef.current = setTimeout(() => {
            recursiveType(sourceText, nextText, setter, timerRef);
          }, typingSpeed);
        } else {
          timerRef.current = null;
        }
      };
      
      // 开始执行打字机效果
      typeText(
        textBuffer.qa, 
        gptAnalysis.qaAnalysis || '', 
        (text) => setGptAnalysis(prev => ({ ...prev, qaAnalysis: text })),
        typingTimerQA
      );
    }
  }, [textBuffer.qa, gptAnalysis.qaAnalysis, typingSpeed]);

  // 在组件顶部添加ref以累积 WebSocket 消息
  const accumulatedContent = useRef({
    executive: '',
    qa: ''
  });

  // 從網路獲取最新逐字稿
  const fetchWebTranscript = async () => {
    const symbolToUse = formData.symbol || companyInfo?.symbol;
    const companyName = companyInfo?.company_name || symbolToUse;

    if (!symbolToUse) {
      setWebTranscript(prev => ({
        ...prev,
        error: '請選擇公司',
        isLoading: false
      }));
      return;
    }

    // AI 使用次數檢查
    const limitCheck = await checkAILimit('AI_Web_Transcript');
    if (!limitCheck.canProceed) {
      setWebTranscript(prev => ({
        ...prev,
        error: limitCheck.reason || 'AI 使用次數已達上限',
        isLoading: false
      }));
      return;
    }

    setWebTranscript(prev => ({
      ...prev,
      isLoading: true,
      error: null,
      result: '',
      isVisible: true
    }));

    try {
      // 構建 prompt
      const prompt = `請提供 ${companyName} (${symbolToUse}) 最新會議紀錄的營運與財務概況、主要業務或市場動態、未來展望與財務指引、管理層問答 (Q&A)、其他重要說明(回購、併購或官司)。請用繁體中文回答，並用HTML格式回覆，使用適當的HTML標籤（如h3、p、ul、li、table等），並加上精簡的內聯樣式。`;

      const messages = [
        {
          role: "system",
          content: "你是一個專業的股票分析師，請根據提供的公司代碼進行分析。請用繁體中文回答，並提供結構化的分析。"
        },
        {
          role: "user",
          content: prompt
        }
      ];

      // API 請求數據
      const requestData = {
        model: API_MODEL,
        messages: messages,
        temperature: 0.3,
        max_tokens: API_MAX_TOKENS,
        stream: true
      };

      const data = JSON.stringify(requestData);

      const response = await fetch(`https://${API_HOST}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${API_KEY}`,
          'Accept': 'text/event-stream',
          'User-Agent': 'Mozilla/5.0 (compatible; FinancialAnalysis/1.0)'
        },
        body: data
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let aggregated = '';

      let isDone = false;
      
      while (!isDone) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        buffer += chunk;
        
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // 保留最後一行（可能不完整）

        for (const line of lines) {
          if (line.trim() === '') continue;
          if (line.trim() === 'data: [DONE]') {
            isDone = true;
            break;
          }

          try {
            const json = JSON.parse(line.replace('data: ', ''));
            const content = json.choices[0]?.delta?.content;
            if (content) {
              // 過濾掉 <think> 標籤內容
              let filteredContent = content
                .replace(/<think>.*?<\/think>/gs, '')
                .replace(/<think>.*$/gs, '')
                .replace(/^.*?<\/think>/gs, '')
                .replace(/<think>/g, '')
                .replace(/<\/think>/g, '');
              
              if (!filteredContent.trim()) {
                continue;
              }
              aggregated += filteredContent;
              
              setWebTranscript(prev => {
                const newResult = prev.result + filteredContent;
                const finalResult = newResult
                  .replace(/<think>.*?<\/think>/gs, '')
                  .replace(/<think>.*$/gs, '')
                  .replace(/^.*?<\/think>/gs, '')
                  .replace(/<think>/g, '')
                  .replace(/<\/think>/g, '');
                
                return {
                  ...prev,
                  result: finalResult
                };
              });
            }
          } catch (e) {
            logger.warn('解析串流數據時出錯:', e);
          }
        }
      }

      // 串流結束
      if (aggregated) {
        const cacheKey = `web_transcript_${symbolToUse}`;
        saveExpiryToCache(cacheKey, {
          symbol: symbolToUse,
          result: aggregated
        }, 10);
        logger.log(`網路逐字稿快取已寫入: ${cacheKey}, 長度=${aggregated.length}`);
      }

      setWebTranscript(prev => ({
        ...prev,
        isLoading: false
      }));

      // 記錄活動
      activityTracker.trackBtnExecution('AI_Web_Transcript', symbolToUse, window.location.pathname);

    } catch (error) {
      logger.error('獲取網路逐字稿時發生錯誤:', error);
      setWebTranscript(prev => ({
        ...prev,
        error: '獲取網路逐字稿失敗，請稍後再試',
        isLoading: false
      }));
    }
  };

  return (
    <div className="transcript-search">
      <div className="content-container">
        <Card 
    
          className="financial-card"
        >
          <form onSubmit={fetchTranscript} className="search-form">
            <div className="form-row">
              <div className="form-group year-quarter-group">
                <label>財務年度:</label>
                <select
                  name="year"
                  value={formData.year}
                  onChange={handleInputChange}
                >
                  {years.map(year => (
                    <option key={year.value} value={year.value}>{year.label}</option>
                  ))}
                </select>
                <label style={{ marginLeft: '10px' }}>季度:</label>
                <select
                  name="quarter"
                  value={formData.quarter}
                  onChange={handleInputChange}
                >
                  {quarters.map(q => (
                    <option key={q.value} value={q.value}>{q.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="button-container">
                <button 
                  type="submit" 
                  disabled={transcriptData.isLoading}
                  style={{ 
                    backgroundColor: '#f5f5f5', 
                    borderColor: '#d9d9d9', 
                    color: '#000000',
                    fontWeight: 'normal'
                  }}
                >
                  {transcriptData.isLoading ? (
                    '查詢中...'
                  ) : (
                    <>
                      <SearchOutlined />
                      {window.innerWidth > 480 && <span style={{ marginLeft: '8px' }}>原文查詢</span>}
                    </>
                  )}
                </button>
                
                <button 
                  type="button" 
                  onClick={fetchGptAnalysis}
                  disabled={gptAnalysis.isLoading || transcriptData.isLoading}
                  className="gpt-button"
                  //style={{ marginLeft: '10px' }}
                  style={{ backgroundColor: '#1890ff', borderColor: 'transparent', fontWeight: 'bold' }}
                >
                  {gptAnalysis.isLoading ? (
                    'AI分析中...'
                  ) : (
                    <>
                      <RobotOutlined />
                      {window.innerWidth > 480 && <span style={{ marginLeft: '8px' }}>AI重點整理</span>}
                    </>
                  )}
                </button>
                <text> （請先「原文查詢」再進行「AI重點整理」）</text>
              </div>
            </div>
            
            {/* 網路獲取逐字稿按鈕 */}
            <div className="form-row" style={{ marginTop: '10px' }}>
            <text style={{ marginLeft: '10px', fontSize: '1.0em', color: '#ffffff' }}>
                若網站內無最新會議紀錄，請點擊此按鈕從網路抓取最新逐字稿 : 
              </text>
              <button 
                type="button" 
                onClick={fetchWebTranscript}
                disabled={webTranscript.isLoading}
                className="web-transcript-button"
                style={{ 
                  backgroundColor: '#ff3a45', 
                  borderColor: '#ff3a45', 
                  color: '#fff',
                  fontWeight: 'bold',
                  padding: '8px 16px',
                  fontSize: '0.7rem',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap'
                }}
              >
                {webTranscript.isLoading ? (
                  '獲取中...'
                ) : (
                  <>
                    <GlobalOutlined />
                    {window.innerWidth > 480 && <span style={{ marginLeft: '8px' }}>擷取網路最新逐字稿</span>}
                  </>
                )}
              </button>
              
            </div>
          </form>

          {/* 網路逐字稿結果顯示區域 */}
          {webTranscript.isVisible && (
            <div className="web-transcript-result" style={{ marginTop: '15px', marginBottom: '15px' }}>
              {webTranscript.error && (
                <Alert
                  message="錯誤"
                  description={webTranscript.error}
                  type="error"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />
              )}
              
              {webTranscript.isLoading && (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <div className="loading-indicator">正在從網路獲取最新逐字稿...</div>
                </div>
              )}
              
              {webTranscript.result && (
                <Card 
                  title="網路最新逐字稿" 
                  size="small"
                  style={{ marginBottom: '16px' }}
                >
                  <div 
                    className="web-transcript-content"
                    style={{ 
                      lineHeight: '1.6',
                      fontSize: '14px'
                    }}
                    dangerouslySetInnerHTML={{ __html: webTranscript.result }}
                  />
                </Card>
              )}
            </div>
          )}

          {/* 添加最新财报信息显示区域 */}
          {transcriptData.latestInfo && (
            <div className="latest-info" style={{ marginTop: '10px', marginBottom: '10px' }}>
              <Alert

                description={
                  <div>
                    <p> 近期會議記錄逐字稿為 「{transcriptData.latestInfo.fiscalYear}  {transcriptData.latestInfo.quarter}」, 會議日期: 「{transcriptData.latestInfo.eventDate}」 , 請點擊原文查詢</p>
                    <p></p>
                  </div>
                }
                type="info"
                showIcon
              />
            </div>
          )}

          <style jsx>{`
            .search-form {
              margin-bottom: 10px;
              width: 100%;
            }
            .financial-card :global(.ant-card-head-title) {
              padding: 12px 0;
            }
            .financial-card :global(.ant-typography) {
              font-size: 1rem;
            }
            @media screen and (max-width: 768px) {
              .financial-card :global(.ant-typography) {
                font-size: 1rem;
              }
            }
            @media screen and (max-width: 480px) {
              .financial-card :global(.ant-typography) {
                font-size: 0.7rem;
              }
              .financial-card :global(.ant-card-head-title) {
                padding: 8px 0;
                font-size: 0.7rem;
              }
            }
            .form-row {
              display: flex;
              align-items: center;
              gap: 15px;
              width: 100%;
            }
            .year-quarter-group {
              display: flex;
              align-items: center;
              gap: 0px;
              flex: 0 0 auto;
            }
            .year-quarter-group label {
              font-size: 1rem;
              white-space: nowrap;
            }
            .year-quarter-group select {
              padding: 4px 8px;
              font-size: 1rem;
              border: 1px solid #d9d9d9;
              border-radius: 4px;
            }
            .button-container {
              display: flex;
              gap: 10px;
              margin-left: 0;
            }
            .button-container button {
              padding: 8px 16px;
              font-size: 0.7rem;
              border: 1px solid #d9d9d9;
              border-radius: 4px;
              background: #1890ff;
              color: white;
              cursor: pointer;
              transition: all 0.3s;
              display: flex;
              align-items: center;
              white-space: nowrap;
              min-width: fit-content;
            }
            .button-container button:hover {
              background: #40a9ff;
            }
            .button-container button[type="submit"]:hover {
              background: #e6e6e6;
              border-color: #bfbfbf;
              color: #000000;
            }
            .button-container button:disabled {
              cursor: not-allowed;
              opacity: 0.5;
            }
            .gpt-button {
              min-width: 90px;
            }
            .web-transcript-button:hover {
              background: #ff6b75 !important;
              border-color: #ff6b75 !important;
            }
            .web-transcript-button:disabled {
              cursor: not-allowed;
              opacity: 0.5;
            }
            .web-transcript-result {
              border: 1px solid #f0f0f0;
              border-radius: 8px;
              background: #fafafa;
              padding: 16px;
            }
            .web-transcript-content {
              color: #262626;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            }
            .web-transcript-content h1, 
            .web-transcript-content h2, 
            .web-transcript-content h3 {
              color: #ff3a45;
              margin-top: 16px;
              margin-bottom: 8px;
            }
            .web-transcript-content p {
              margin-bottom: 8px;
            }
            .web-transcript-content ul, 
            .web-transcript-content ol {
              margin-left: 20px;
              margin-bottom: 8px;
            }

            @media screen and (max-width: 768px) {
              .search-form {
                margin-bottom: 5px;
              }
              .form-row {
                gap: 8px;
                font-size: 0.6rem;
                justify-content: center;
              }
              .year-quarter-group {
                gap: 0px;
              }
              .year-quarter-group label {
                font-size: 0.7rem;
              }
              .year-quarter-group select {
                font-size: 0.6rem;
                max-width: 60px;
                line-height: 1.1;
                padding: 2px 4px;
              }
              .button-container {
                gap: 5px;
              }
              .button-container button {
                padding: 6px 12px;
                min-width: fit-content;
              }
              .gpt-button {
                min-width: 80px;
              }
              .web-transcript-button {
                padding: 6px 12px !important;
                font-size: 0.6rem !important;
              }
            }

            @media screen and (max-width: 480px) {
              .search-form {
                margin-bottom: 4px;
              }
              .form-row {
                gap: 15px;
                justify-content: center;
                flex-wrap: nowrap;
                width: 100%;
              }
              .year-quarter-group {
                gap: 0px;
                justify-content: right;
                width: 100%;
              }
              .year-quarter-group label {
                font-size: 0.6rem;
              }
              .year-quarter-group select {
                font-size: 0.6rem;
                padding: 1px 2px;
                width: 25px;
              }
              .button-container {
                gap: 4px;
                justify-content: right;
                margin-top: 4px;
              }
              .button-container button {
                padding: 4px 8px;
                min-width: fit-content;
              }
              .gpt-button {
                min-width: 70px;
              }
              .web-transcript-button {
                padding: 4px 8px !important;
                font-size: 0.6rem !important;
              }
            }
          `}</style>
        </Card>

        {/* GPT 分析結果顯示區域 */}
        {(textBuffer.executive || textBuffer.qa || gptAnalysis.isLoading) && (
          <div className="gpt-analysis-container" style={{ marginTop: '20px', marginBottom: '20px' }}>
            <div className="gpt-analysis-header">
              <h3 style={{ fontSize: window.innerWidth <= 480 ? '0.8rem' : window.innerWidth <= 768 ? '1rem' : '1.1rem' }}>逐字稿分析</h3>
            </div>
            {gptAnalysis.isLoading && <div className="loading-indicator"> 正在分析中...</div>}
            
            {/* 使用 textBuffer 判斷是否顯示高管分析 */}
            {textBuffer.executive && (
              <div className="executive-analysis">
                <div 
                  style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    cursor: 'pointer',
                    marginBottom: '10px'
                  }}
                  onClick={() => setIsExecutiveExpanded(!isExecutiveExpanded)}
                >
                  <h4 style={{ 
                    fontSize: window.innerWidth <= 480 ? '0.8rem' : window.innerWidth <= 768 ? '0.9rem' : '0.9rem',
                    margin: 0
                  }}>公司高層陳述：</h4>
                  {isExecutiveExpanded ? <UpOutlined /> : <DownOutlined />}
                </div>
                {isExecutiveExpanded && (
                  <div className="analysis-content" style={{ 
                    whiteSpace: 'pre-wrap',
                    fontSize: window.innerWidth <= 480 ? '0.8rem' : window.innerWidth <= 768 ? '0.9rem' : '0.9rem',
                    lineHeight: window.innerWidth <= 480 ? '1.6' : '1.8'
                  }}>
                    {gptAnalysis.executiveAnalysis}
                    {/* 添加游標效果 */}
                    {gptAnalysis.executiveAnalysis !== textBuffer.executive && 
                      <span className="typing-cursor"></span>}
                  </div>
                )}
              </div>
            )}
            
            {/* 使用 textBuffer 判斷是否顯示問答分析 */}
            {textBuffer.qa && (
              <div className="qa-analysis">
                <div 
                  style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    cursor: 'pointer',
                    marginBottom: '10px'
                  }}
                  onClick={() => setIsQAExpanded(!isQAExpanded)}
                >
                  <h4 style={{ 
                    fontSize: window.innerWidth <= 480 ? '0.8rem' : window.innerWidth <= 768 ? '0.9rem' : '0.9rem',
                    margin: 0
                  }}>問答環節：</h4>
                  {isQAExpanded ? <UpOutlined /> : <DownOutlined />}
                </div>
                {isQAExpanded && (
                  <div className="analysis-content" style={{ 
                    whiteSpace: 'pre-wrap',
                    fontSize: window.innerWidth <= 480 ? '0.8rem' : window.innerWidth <= 768 ? '0.9rem' : '0.9rem',
                    lineHeight: window.innerWidth <= 480 ? '1.6' : '1.8'
                  }}>
                    {gptAnalysis.qaAnalysis}
                    {/* 添加游標效果 */}
                    {gptAnalysis.qaAnalysis !== textBuffer.qa && 
                      <span className="typing-cursor"></span>}
                  </div>
                )}
              </div>
            )}
            
            {gptAnalysis.error && (
              <div className="error-message">
                {gptAnalysis.error}
              </div>
            )}
          </div>
        )}

        {transcriptData.error && (
          <div className="error-message">{transcriptData.error}</div>
        )}

        {/* 添加显示模式切换按钮 */}
        {transcriptData.transcripts.length > 0 && (
          <div className="display-mode-selector">
            <label>顯示模式：</label>
            <div className="display-mode-buttons">
              <button 
                className={`mode-button ${displayMode === 'both' ? 'active' : ''}`}
                onClick={() => setDisplayMode('both')}
              >
                中英對照
              </button>
              <button 
                className={`mode-button ${displayMode === 'cn' ? 'active' : ''}`}
                onClick={() => setDisplayMode('cn')}
              >
                僅中文
              </button>
              <button 
                className={`mode-button ${displayMode === 'en' ? 'active' : ''}`}
                onClick={() => setDisplayMode('en')}
              >
                僅英文
              </button>
            </div>
          </div>
        )}

        <style jsx>{`
          .display-mode-selector {
            margin-bottom: 20px;
          }
          .display-mode-selector label {
            margin-right: 10px;
            font-size: 0.8rem;
          }
          .display-mode-buttons {
            display: inline-flex;
            gap: 10px;
          }
          .mode-button {
            padding: 8px 16px;
            border: 1px solid #d9d9d9;
            border-radius: 4px;
            background: #fff;
            cursor: pointer;
            font-size: 1rem;
            transition: all 0.3s;
          }
          .mode-button.active {
            background: #1890ff;
            color: white;
            border-color: #1890ff;
          }
          @media screen and (max-width: 768px) {
            .display-mode-selector label {
              font-size: 0.6rem;
            }
            .mode-button {
              padding: 6px 12px;
              font-size: 0.6rem;
            }
            .display-mode-buttons {
              gap: 6px;
            }
          }
          @media screen and (max-width: 480px) {
            .display-mode-selector label {
              font-size: 0.7rem;
            }
            .mode-button {
              padding: 4px 8px;
              font-size: 0.7rem;
            }
            .display-mode-buttons {
              gap: 4px;
            }
          }
        `}</style>

        {transcriptData.transcripts.length > 0 && (
          <div className="table-container">
            <table className={displayMode === 'both' ? 'dual-language' : 'single-language'}>
              <thead>
                <tr>
                  <th>內容類型</th>
                  {/* 根据显示模式动态显示表头 */}
                  {displayMode !== 'cn' && (
                    <th>英文內容</th>
                  )}
                  {displayMode !== 'en' && (
                    <th>
                      中文內容
                      {isTranslated && (
                        <span className="translation-indicator" title="機器翻譯結果">
                          ●
                        </span>
                      )}
                      {transcriptData.transcripts[0]?.eventdate && (
                        <span className="event-date" style={{ color: '#faad14', fontWeight: 'bold' }}>
                          (財報季度及日期：{transcriptData.year} {transcriptData.quarter} {new Date(transcriptData.transcripts[0].eventdate).toLocaleDateString()})
                        </span>
                      )}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const qaCount = {}; // 本地变量追踪当前渲染过程中每种内容类型的计数
                  
                  return transcriptData.transcripts.map((group, groupIndex) => {
                    // 处理内容类型并添加编号
                    let displayType = group.contentType;
                    
                    // 检查是否是 QA 时间类型
                    if (displayType === 'QA 問答' || displayType === 'QA时间' || displayType === 'qa_session') {
                      // 如果是第一次出现这个类型，初始化为1，否则增加计数
                      if (!qaCount[displayType]) {
                        qaCount[displayType] = 1;
                      } else {
                        qaCount[displayType]++;
                      }
                      
                      // 根据内容类型进行格式化并添加编号
                      if (displayType === 'qa_session') {
                        displayType = `QA 問答${qaCount[displayType]}`;
                      } else {
                        displayType = `${displayType}${qaCount[displayType]}`;
                      }
                    } else if (displayType === 'executive') {
                      displayType = '公司高層說法';
                    }
                    
                    return group.enParagraphs.map((enParagraph, paraIndex) => (
                      <tr key={`${groupIndex}-${paraIndex}`}>
                        {paraIndex === 0 && (
                          <td 
                            rowSpan={group.enParagraphs.length} 
                            className={`content-type ${displayType.includes('QA') ? 'qa-session' : ''}`}
                          >
                            {displayType}
                          </td>
                        )}
                        {/* 根据显示模式动态显示内容 */}
                        {displayMode !== 'cn' && (
                          <td className="content-cell">
                            {processEnglishParagraph(enParagraph)}
                          </td>
                        )}
                        {displayMode !== 'en' && (
                          <td className="content-cell">
                            {group.twParagraphs[paraIndex] 
                              ? processChineseParagraph(group.twParagraphs[paraIndex])
                              : ''}
                          </td>
                        )}
                      </tr>
                    ));
                  }).flat();
                })()}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <AIUpgradePrompt
        visible={showUpgradePrompt}
        onCancel={() => setShowUpgradePrompt(false)}
        limitInfo={limitInfo}
      />
    </div>
  );
}

export default App_transcript; 
